import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Save, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { faqService } from '../services/api';
import { FAQItem } from '../types/api';
import LoadingSpinner from './ui/LoadingSpinner';
import { FAQ_CATEGORIES } from '../utils/constants';

const AdminFAQ: React.FC = () => {
  const [faqData, setFaqData] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQItem | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<FAQItem>>({
    question: '',
    answer: '',
    category: FAQ_CATEGORIES[0]
  });

  // Carregar dados ao montar o componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await faqService.getAll();
        if (response.success && response.data) {
          setFaqData(response.data);
        } else {
          console.error('Erro ao carregar FAQ:', response.error);
        }
      } catch (error) {
        console.error('Erro ao carregar FAQ:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleAddNew = () => {
    setFormData({
      question: '',
      answer: '',
      category: FAQ_CATEGORIES[0]
    });
    setEditingFAQ(null);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleEdit = (faq: FAQItem) => {
    setFormData(faq);
    setEditingFAQ(faq);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta pergunta?')) {
      try {
        const response = await faqService.delete(id);
        if (response.success) {
          setFaqData(prev => prev.filter(faq => faq.id !== id));
          alert('Pergunta excluída com sucesso!');
        } else {
          alert('Erro ao excluir pergunta: ' + response.error);
        }
      } catch (error) {
        console.error('Erro ao deletar FAQ:', error);
        alert('Erro ao excluir pergunta. Tente novamente.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let response: any;
      
      if (isEditing && editingFAQ) {
        // Editar FAQ existente
        response = await faqService.update(editingFAQ.id, formData);
        if (response.success && response.data) {
          setFaqData(prev => prev.map(faq => faq.id === editingFAQ.id ? response.data : faq));
          alert('Pergunta atualizada com sucesso!');
        } else {
          alert('Erro ao atualizar pergunta: ' + response.error);
          return;
        }
      } else {
        // Criar nova FAQ
        response = await faqService.create({
          question: formData.question || '',
          answer: formData.answer || '',
          category: formData.category || FAQ_CATEGORIES[0]
        });
        if (response.success && response.data) {
          setFaqData(prev => [...prev, response.data]);
          alert('Pergunta criada com sucesso!');
        } else {
          alert('Erro ao criar pergunta: ' + response.error);
          return;
        }
      }
      
      setShowForm(false);
      setFormData({});
      setEditingFAQ(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao salvar FAQ:', error);
      alert('Erro ao salvar pergunta. Tente novamente.');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <LoadingSpinner message="Carregando FAQ..." />
        </div>
      </section>
    );
  }

  return (
    <section className="section bg-brand-sand pt-32 min-h-screen">
      <div className="wrapper">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="btn-secondary">
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Link>
              <div>
                <h1 className="text-3xl font-heading text-brand-slate">Gerenciar FAQ</h1>
                <p className="text-brand-ink/70">Criar, editar e excluir perguntas frequentes</p>
              </div>
            </div>
            <button onClick={handleAddNew} className="btn-primary">
              <Plus className="w-4 h-4" />
              Nova Pergunta
            </button>
          </motion.div>

          {/* Form Modal */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-heading text-brand-slate">
                    {isEditing ? 'Editar Pergunta' : 'Nova Pergunta'}
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-brand-ink/60 hover:text-brand-ink"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Pergunta */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-brand-slate">Pergunta</label>
                    <input
                      type="text"
                      value={formData.question || ''}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-lg font-medium text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                      placeholder="Ex: Quais são os meus direitos em caso de demissão?"
                      required
                    />
                  </div>

                  {/* Categoria */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-brand-slate">Categoria</label>
                    <div className="relative">
                      <select
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate bg-transparent appearance-none cursor-pointer transition-colors"
                        required
                      >
                        {FAQ_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-brand-ink/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Resposta */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-brand-slate">Resposta</label>
                    <textarea
                      value={formData.answer || ''}
                      onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                      className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-y min-h-[150px] bg-transparent"
                      rows={6}
                      placeholder="Forneça uma resposta clara e detalhada..."
                      required
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button type="submit" className="btn-primary flex-1">
                      <Save className="w-4 h-4" />
                      {isEditing ? 'Salvar Alterações' : 'Criar Pergunta'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}

          {/* FAQ List */}
          <motion.div variants={itemVariants} className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={faq.id} className="card card-pad">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="badge text-xs">{faq.category || 'Geral'}</span>
                      <span className="text-brand-ink/60 text-sm">#{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-heading text-brand-slate mb-3">
                      {faq.question}
                    </h3>
                    <p className="text-brand-ink/80 text-sm line-clamp-3">
                      {faq.answer}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(faq)}
                      className="p-2 text-brand-slate hover:text-brand-accent transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AdminFAQ;
